import announceFormStatus, { clearFormStatus } from '../../form_status';
import {
  RECAPTCHA_UNAVAILABLE,
  loadRecaptchaApi,
  requestRecaptchaToken,
} from '../../lib/recaptcha_token';

function reportRecaptchaFailure(error) {
  if (!window.Sentry) {
    return;
  }

  if (error?.message === RECAPTCHA_UNAVAILABLE) {
    window.Sentry.captureMessage(RECAPTCHA_UNAVAILABLE);
  } else {
    window.Sentry.captureException(error);
  }
}

function initRecaptchaForm(recaptchaInput) {
  const siteKey = recaptchaInput.dataset.recaptchaSiteKey;
  const action = recaptchaInput.dataset.recaptchaAction;

  if (!siteKey || !action) {
    return;
  }

  const form = recaptchaInput.closest('form');
  if (!form) {
    return;
  }

  if (form.dataset.recaptchaInitialized) {
    return;
  }
  form.dataset.recaptchaInitialized = 'true';

  // Fetch api.js while the browser is idle so a token is ready even if the user
  // focuses and submits before the focusin-triggered load.
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(function prewarmRecaptcha() {
      loadRecaptchaApi(siteKey);
    });
  } else {
    setTimeout(function prewarmRecaptcha() {
      loadRecaptchaApi(siteKey);
    }, 0);
  }

  form.addEventListener(
    'focusin',
    function loadOnFirstInteraction() {
      loadRecaptchaApi(siteKey);
    },
    { once: true },
  );

  let isSubmitting = false;

  form.addEventListener('submit', function handleSubmit(e) {
    if (recaptchaInput.value) {
      return;
    }

    if (isSubmitting) {
      e.preventDefault();
      return;
    }

    e.preventDefault();
    isSubmitting = true;
    announceFormStatus('verifying');

    // A lazily loaded validator cancels the submission after this listener, and
    // form.submit() ignores it: settle validity before asking for a token.
    setTimeout(function requestToken() {
      // A novalidate form turned constraint validation off: its own validator decides.
      if (!form.noValidate && !form.checkValidity()) {
        isSubmitting = false;
        clearFormStatus('verifying');
        return;
      }

      requestRecaptchaToken(recaptchaInput)
        .then(function fillToken(token) {
          recaptchaInput.value = token;
        })
        .catch(reportRecaptchaFailure)
        .finally(function submitForm() {
          form.submit();
        });
    }, 0);
  });

  // A bfcache restore hands back a v3 token the server already consumed:
  // resubmitting it would fail verification, so the next submit must refetch one.
  window.addEventListener('pageshow', function releaseRecaptcha(event) {
    if (!event.persisted) return;

    recaptchaInput.value = '';
    isSubmitting = false;
    clearFormStatus('verifying');
  });
}

document.addEventListener('DOMContentLoaded', function initRecaptcha() {
  document
    .querySelectorAll('input[data-recaptcha-action]')
    .forEach(initRecaptchaForm);
});
